<template>
  <v-dialog v-model="visible"
    persistent
    scrollable>
    <v-card :loading="loading">
      <v-card-title> New Cases </v-card-title>
      <v-card-text>
        <v-tabs>
          <v-tab href="#newCase">New Case</v-tab>
          <v-tab-item value="newCase">
            <v-card flat>
              <v-card-text>
                <!-- New case form -->
                <v-form ref="newCaseForm">
                  <v-row>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="20"
                        :label="$t('modal.addCase.label.localId')"
                        v-model="newCase.localId"
                        :hint="$t('hint.localId')"
                        persistent-hint />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="20"
                        :label="$t('label.title')"
                        v-model="newCase.title" />
                    </v-col>
                    <v-spacer />
                    <v-col cols="7">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('label.name')"
                        v-model="newCase.name"
                        :rules="[rules.required]" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="4">
                      <v-select dense
                        :items="types"
                        v-model="newCase.type"
                        :label="$t('label.type')" />
                    </v-col>
                    <v-spacer />
                    <v-col cols="3">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.placeOfBirth')"
                        v-model="newCase.placeOfBirth" />
                    </v-col>
                    <v-col cols="4">
                      <v-menu v-model="dobPicker"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field dense
                            clearable
                            v-model="newCase.dob"
                            :label="$t('modal.addCase.label.dob')"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"></v-text-field>
                        </template>
                        <v-date-picker no-title
                          v-model="newCase.dob"
                          :show-current="false"
                          @input="dobPicker = false">
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.citizenship')"
                        v-model="newCase.citizenship" />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.phoneNumber')"
                        v-model="newCase.phoneNumber" />
                    </v-col>
                    <v-col cols="2">
                      <v-select dense
                        clearable
                        :items="genders"
                        v-model="newCase.gender"
                        :label="$t('modal.addCase.label.gender')" />
                    </v-col>
                  </v-row>

                  <!-- Client custom labels -->
                  <v-row v-if="getAccountSettings.customLabels">
                    <v-col cols="3"
                      v-for="(value, labelName) in getAccountSettings.customLabels"
                      :key="labelName">
                      <v-select :items=value
                        :label=labelName
                        v-model="customLabels[labelName]"
                        clearable
                        dense></v-select>
                    </v-col>
                  </v-row>

                  <!-- Client custom fields -->
                  <v-row v-if="getAccountSettings.customFields">
                    <v-col cols="3"
                      v-for="(fieldName) in getAccountSettings.customFields"
                      :key="fieldName">
                      <v-text-field dense
                        :label="fieldName"
                        v-model="customFields[fieldName]" />
                    </v-col>
                  </v-row>

                  <v-row align="center">
                    <v-col cols="2">
                      <h3>{{ $t("label.address") }}</h3>
                    </v-col>
                    <v-col>
                      <!-- <v-divider /> -->
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address1')"
                        v-model="newCase.address.address1" />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address2')"
                        v-model="newCase.address.address2" />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address3')"
                        v-model="newCase.address.address3" />
                    </v-col>
                    <v-col cols="2">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.address4')"
                        v-model="newCase.address.address4" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="3">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.city')"
                        v-model="newCase.address.city" />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.stateOrProvince')"
                        v-model="newCase.address.stateOrProvince" />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.postalCode')"
                        v-model="newCase.address.postalCode" />
                    </v-col>
                    <v-col cols="3">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.country')"
                        v-model="newCase.address.country" />
                    </v-col>
                  </v-row>

                  <!-- Tax IDs -->
                  <v-row>
                    <v-col cols="6">
                      <v-row align="center">
                        <v-col>
                          <h3>Tax IDs</h3>
                        </v-col>
                        <v-col cols="1">
                          <v-btn color="green lighten-1"
                            icon
                            @click="addId">
                            <v-icon dark>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-row v-if="!caseIds || caseIds.length == 0"
                        dense>
                        <v-alert dense
                          outlined
                          type="info">Click <v-icon small
                            color="success">mdi-plus</v-icon> to add tax IDs.</v-alert>
                      </v-row>

                      <v-row v-for="(caseId, index) in caseIds"
                        :key="index">
                        <v-col cols="4">
                          <v-select dense
                            :items="idTypes"
                            v-model="caseId.type"
                            :label="$t('label.type')" />
                        </v-col>
                        <v-col>
                          <v-text-field dense
                            label="Country"
                            hide-details
                            :maxlength="100"
                            v-model="caseId.country" />
                        </v-col>
                        <v-col>
                          <v-text-field label="ID Number"
                            hide-details
                            dense
                            :maxlength="100"
                            v-model="caseId.idNumber" />
                        </v-col>
                        <v-col cols="1">
                          <v-btn color="red lighten-1"
                            dark
                            icon
                            @click="deleteId(index)">
                            <v-icon dark>mdi-close</v-icon>
                          </v-btn>
                        </v-col>

                      </v-row>
                    </v-col>

                    <!-- cryptopWallet -->
                    <v-col>
                      <v-row dense>
                        <v-col>
                          <h3>Crypto Wallet</h3>
                          <v-row nogutters>
                            <v-col cols="4">
                              <v-text-field :maxlength="100"
                                label="Name"
                                v-model="newCase.cryptoWallet.walletName" />
                            </v-col>
                            <v-col cols="8">
                              <v-text-field :maxlength="200"
                                label="Wallet ID"
                                v-model="newCase.cryptoWallet.walletId" />
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <v-row align="center">
                    <v-col cols="2">
                      <h3>Other IDs</h3>
                    </v-col>
                    <v-col>
                      <!-- <v-divider /> -->
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="6">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.passportCountry')"
                        v-model="newCase.passport.passportCountry" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.passportNumber')"
                        v-model="newCase.passport.passportNumber" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.licenseCountry')"
                        v-model="newCase.driverLicense.licenseCountry" />
                    </v-col>
                    <v-col cols="6">
                      <v-text-field dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.licenseNumber')"
                        v-model="newCase.driverLicense.licenseNumber" />
                    </v-col>
                    <!-- <v-col cols="6">
                      <v-text-field
                        dense
                        :maxlength="100"
                        :label="$t('modal.addCase.label.ssn')"
                        v-model="newCase.ssn"
                      />
                    </v-col> -->
                  </v-row>

                  <!-- compliance and attributes -->
                  <v-row dense>
                    <v-col cols="5">
                      <v-row align="center">
                        <v-col cols="4">
                          <h3>{{ $t("label.compliance") }}</h3>
                        </v-col>
                        <v-col>
                          <!-- <v-divider /> -->
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <v-autocomplete v-model="newCase.caseGroupId"
                            :items="caseGroups"
                            :label="$t('modal.addCase.label.caseGroups')"
                            :hint="$t('hint.caseGroups')"
                            clearable
                            persistent-hint
                            item-text="name"
                            item-value="id"
                            dense />
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-spacer />

                    <v-col cols="6">
                      <!-- attributes -->
                      <v-row align="center"
                        dense>
                        <v-col cols="3">
                          <h3>
                            {{ $t("modal.addCase.label.customAttributes") }}
                          </h3>
                        </v-col>
                        <v-col>
                          <!-- <v-divider /> -->
                        </v-col>
                        <v-col cols="1">
                          <v-btn color="green lighten-1"
                            icon
                            @click="addAttribute">
                            <v-icon dark>mdi-plus</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>

                      <v-row v-if="!customAttributes || customAttributes.length == 0"
                        dense>
                        <v-alert dense
                          outlined
                          type="info">Click <v-icon small
                            color="success">mdi-plus</v-icon> to add attributes.</v-alert>
                      </v-row>

                      <v-row dense
                        v-for="(attr, index) in customAttributes"
                        :key="index">
                        <v-col>
                          <v-text-field outlined
                            dense
                            hide-details
                            :maxlength="100"
                            v-model="attr.key" />
                        </v-col>
                        <v-col>
                          <v-text-field outlined
                            dense
                            hide-details
                            :maxlength="100"
                            v-model="attr.value" />
                        </v-col>
                        <v-col cols="1">
                          <v-btn color="red lighten-1"
                            dark
                            icon
                            @click="deleteAttribute(index)">
                            <v-icon dark>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn color="blue darken-1"
                  text
                  @click="onCancel">{{
                    $t("label.cancel")
                  }}</v-btn>
                <v-btn color="blue darken-1"
                  @click="onSave"
                  :loading="loading"
                  dark>{{ $t("label.save") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-tab-item>

          <v-tab href="#uploadCase"
            :disabled="templated">Import Cases</v-tab>
          <v-tab-item value="uploadCase">
            <CaseCSVForm pageTitle="Import Cases"
              v-on:onCancel="onCancel"
              v-on:onConfirmed="onCsvUploadConfirmed"
              :buttonsDisabled="loading" />
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapActions, mapGetters } from "vuex";
  import { getMinDateString } from "../../utils/dateUtils";
  import CaseCSVForm from "../../components/CaseCSVForm";
  const model = { key: "", value: "" };
  const idStruct = { type: "", country: "", idNumber: "" };
  export default {
    name: "AddCase",
    components: { CaseCSVForm },

    props: {
      parentCaseReportId: String, // If this case record is based on the search case of an existing report.
      visible: {
        type: Boolean,
        default: false,
      },
      templated: {
        type: Boolean,
        default: false,
      },
      templateCase: {
        type: Object,
        default() {
          return {};
        },
      },
      caseGroups: {},
    },

    data() {
      return {
        //customAttributes: [{ key: null, value: null }], // null values avoids saving empty attributes, ex: {"":""}
        customAttributes: [],
        customLabels: {},
        customFields: {},
        caseIds: [],
        newCase: {
          ...this.templateCase,
          address: this.templateCase.address ? this.templateCase.address : {},
          passport: this.templateCase.passport ? this.templateCase.passport : {},
          driverLicense: this.templateCase.driverLicense
            ? this.templateCase.driverLicense
            : {},
          cryptoWallet: {}
        },
        loading: false,
        dobPicker: false,
        rules: {
          required: (value) => !!value || "Required."
        },
        types: ["Individual", "Entity", "Vessel"],
        idTypes: ["VAT", "TIN"],
        genders: ["Male", "Female"],
      };
    },
    watch: {
      templateCase: function () {
        this.newCase = {
          ...this.templateCase,
          address: this.templateCase.address ? this.templateCase.address : {},
          passport: this.templateCase.passport ? this.templateCase.passport : {},
          driverLicense: this.templateCase.driverLicense
            ? this.templateCase.driverLicense
            : {},
        };
      },
    },
    computed: {
      ...mapGetters(["getProfile", "getAccountSettings"]),
    },

    created() {
      // If template case has attributes, clear default attribute and populate with existing attributes
      if (
        this.templateCase.caseAttributes &&
        Object.keys(this.templateCase.caseAttributes).length > 0
      ) {
        this.customAttributes = [];
        Object.entries(this.templateCase.caseAttributes).map(([key, value]) => {
          this.customAttributes.push({ key: key, value: value });
        });
      }
    },

    methods: {
      ...mapActions([
        "addCase",
        "addCaseIdToReport",
        "addCases",
        "setGlobalAlert",
      ]),
      getMinDateString,
      addAttribute() {
        this.customAttributes.push({ ...model });
      },
      deleteAttribute(index) {
        this.customAttributes.splice(index, 1);
      },
      addId() {
        this.caseIds.push({ ...idStruct });
      },
      deleteId(index) {
        this.caseIds.splice(index, 1);
      },
      //submit - TODO catch error if case group was deleted prior to saving case
      async onSave() {

        if (!this.$refs.newCaseForm.validate()) {
          return false;
        }

        this.loading = true;

        this.newCase.customLabels = this.customLabels; // It would be goot to block empty label arrays here at some point. 
        this.newCase.customFields = this.customFields;

        // set caseAttributes
        // TODO need to block blank attributes - maybe should do this on the back end
        this.newCase.caseAttributes = Object.fromEntries(
          this.customAttributes.map(({ key, value }) => [key, value]) // iterate customAttributes, ex: {key:"theKey", value:"theValue"} => ["theKey":"theValue"]
        );

        this.newCase.identification = this.caseIds;

        // If creating a case based on a report, set initial screening date.
        // TODO this sets lastSceenDate to time when caseRecord is created, not when screening took place. The accurate time can be pulled from searchRecord searchDate
        if (this.templated) {
          this.newCase.lastScreenDate = new Date();
        }

        // let caseRecordRequest = JSON.parse(JSON.stringify(this.newCase));

        let caseRecordRequest = { ...this.newCase }

        // Do not send a DL without a number - DL is treated as an ID in search logic, therefor the number is required.
        // TODO Add requirement note/hint to UI
        if (!this.newCase.driverLicense.licenseNumber) {
          caseRecordRequest.driverLicense = null
        }

        try {
          await this.addCase({
            caseRecord: caseRecordRequest,
            caseReportId: this.parentCaseReportId,
          }).then((res) => {
            // Object properties need to be initialized, or models such as driverLicense.country break
            this.newCase = {
              address: {},
              passport: {},
              driverLicense: {},
              cryptoWallet: {}
            };

            if (this.parentCaseReportId) {
              this.addCaseIdToReport({
                caseReportId: this.parentCaseReportId,
                caseId: this.newCase.id,
              });
            }

            this.$emit("onConfirmed", res.id);
            this.setGlobalAlert({
              alert: true,
              color: "green",
              alertMessage: "Case Added",
            });

          });
        } catch (e) {
          if (e.response.data) {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertMessage: e.response.data,
            });
            return;
          } else {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertMessage: this.$t("error.general.unhandled"),
            });
          }
          return;
        } finally {
          this.loading = false;
        }

        this.$refs.newCaseForm.reset(); // caseRecord from form is cloned and sent to store to avoid changes being made to whats in the store
        this.customAttributes = []
        this.caseIds = []
      },
      onCsvUploadConfirmed(cases) {
        this.loading = true;

        this.addCases(cases)
          .then((res) => {
            this.$emit("onConfirmed");
            this.setGlobalAlert({
              alert: true,
              color: "green",
              alertMessage: res + " Cases Added",
            });
          })
          .catch((e) => {
            console.error("Case API error", e);
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertMessage:
                "Error importing cases, please try again or contact support",
            });
          })
          .finally(() => {
            this.loading = false;
          });
      },
      onCancel() {
        this.$emit("onCancelled");
        // If templated (opened from case report with case template), do not reset form. Only reset form if opened from Case Manager with no template.
        if (!this.templated) {
          this.$refs.newCaseForm.reset();
          // this.customAttributes = [{ key: null, value: null }] // null values avoids saving empty attributes, ex: {"":""}
          this.customAttributes = []
          this.caseIds = []
        }
      },
    },
  };
</script>